<script lang="ts" setup>
import messaging from "@/plugins/firebase";
import { getToken } from "firebase/messaging";
import * as Sentry from "@sentry/vue";
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { useDisplay } from "vuetify";
import dayjs from "dayjs";
import SidebarComponent from "@/components/sidebars/SidebarComponent.vue";
import ChatDialog from "@/views/chats/components/dialogs/ChatDialog.vue";
import GalleryView from "@/components/gallery/GalleryView.vue";
import OmniSnackbar from "@/components/omni/OmniSnackbar.vue";
import type { ChatStateThread } from "@/store/chat.store";
import { useChatStore } from "@/store/chat.store";
import { type AppStateDispatcher, useAppStore } from "@/store/app.store";
import { useGalleryStore } from "@/store/gallery.store";
import { useTripStore } from "@/store/trip.store";
import { useTruckStore } from "@/store/truck.store";
import { useBaseView, useChat, useChatSocket, useDateTime } from "@/composables";
import { useSocketEvent } from "@/composables/useSocketEvent";
import { WSConnection } from "@/plugins/ws.connection";
import { TripService } from "@/services/socket/trip.service";
import { TruckService } from "@/services/socket/truck.service";
import { API } from "@/api/api";
import type { DispatcherPermissionValue } from "@/api/dto/dispatcher.dto";
import type { UserDto } from "@/api/dto/user.dto";

defineOptions({ name: "CabinetLayout" });

const localeField = "lang";

const route = useRoute();
const router = useRouter();
const { locale } = useI18n({ useScope: "global" });
const display = useDisplay();
const api = new API();

const chatStore = useChatStore();
const appStore = useAppStore();
const galleryStore = useGalleryStore();
const tripStore = useTripStore();
const truckStore = useTruckStore();

const { getShortTZForDate } = useDateTime();
const { hasAccess } = useBaseView();
const { handleChatSocket, disconnectChat } = useChat();
const { t } = useI18n();

const { onMessage, onMessages, onThread, onThreads, onOnlineStatus, onRecovery } = useChatSocket();

useSocketEvent("chat:message", ({ message }) => onMessage(message));
useSocketEvent("chat:messages", ({ messages }) => onMessages(messages));
useSocketEvent("chat:thread", ({ thread }) => onThread(thread));
useSocketEvent("chat:threads", ({ threads }) => onThreads(threads));
useSocketEvent("chat:online-status", ({ isOnline }) => onOnlineStatus(isOnline));
useSocketEvent("chat:recovery", ({ lastEvent }) => onRecovery(lastEvent));

const showChatDialog = ref(false);
const showNotificationsSetup = ref(false);
const userSidebarCollapsed = ref<boolean | null>(true);
const sidebarSmall = ref(true);
const timezoneTime = ref("");
const timezoneName = ref("");

let timer: ReturnType<typeof setInterval> | undefined;

const dispatcher = computed(() => appStore.dispatcher);
const headerTitle = computed(() => appStore.headerTitle);
const media = computed(() => galleryStore.media);
const mediaId = computed(() => galleryStore.mediaId);
const mobile = computed(() => display.mobile.value);
const chatUser = computed(() => chatStore.chatUser);
const offline = computed({
  get: () => chatStore.offline,
  set: (value) => chatStore.setOffline(value),
});

const viewHasAccess = computed(() => {
  if (!route.meta.permission) {
    return true;
  }

  return hasAccess(route.meta.permission as DispatcherPermissionValue | DispatcherPermissionValue[]);
});

const openThreadsCount = computed(() => {
  let count = 0;
  for (const thread of chatStore.threads) {
    if ((thread as ChatStateThread & { status?: string }).status === "o") {
      count++;
    }
  }
  return count;
});

function setMediaId(id: number | null) {
  galleryStore.setMediaId(id as number);
}

function setLocale(value: string) {
  locale.value = value;
  dayjs.locale(value);
}

function calculateTime() {
  if (!appStore.dispatcher) {
    return;
  }
  timezoneTime.value = dayjs().tz(appStore.dispatcher.timezone).format("HH:mm");
}

async function loadDispatcher(): Promise<boolean> {
  let profile = null;
  try {
    const resp = await api.dispatchers.getCurrentDispatcher();
    profile = resp.data ?? null;
  } catch {
    profile = null;
  }

  if (profile) {
    appStore.setDispatcher(profile as unknown as AppStateDispatcher);
    Sentry.setUser({
      email: profile.email,
      id: profile.id,
      companyId: profile.companyId,
      companyName: profile.company?.name,
      roles: profile.roles,
    });
    timezoneName.value = getShortTZForDate(dayjs().toDate(), profile.timezone);
    return true;
  } else {
    api.client.saveToken("");
    router.replace({ name: "login" });
    return false;
  }
}

async function loadDispatchers() {
  try {
    const resp = await api.dispatchers.findDispatchers({ pageSize: 0 });
    appStore.setDispatchers(resp.data?.items ?? []);
  } catch (err) {
    console.error("Error load account info", err);
  }
}

function handleTripMonitorSocket(connection: WSConnection) {
  const tripMonitorService = new TripService(connection);
  tripStore.setTripMonitorService(tripMonitorService);
  tripMonitorService.subscribe();
}

function handleTruckSocket(connection: WSConnection) {
  const truckService = new TruckService(connection);
  truckStore.setTruckWSService(truckService);
  truckService.subscribe();
}

async function loadData() {
  await loadDispatchers();
}

async function connectWebSocket() {
  const connection = new WSConnection();

  await connection.connect(api.client.getWSChatUrl(), () => api.client.getToken());

  await handleChatSocket(connection);
  handleTripMonitorSocket(connection);
  handleTruckSocket(connection);
}

async function logout() {
  try {
    const signOutResult = await api.client.signOut();
    if (signOutResult) {
      await router.replace({ name: "login" });
    }
  } catch (error) {
    console.error("ERROR During logout:", error);
  }
}

function setupNotifications() {
  Notification.requestPermission()
    .then((permission) => {
      if (permission !== "granted") {
        return null;
      }

      return getToken(messaging);
    })
    .then((token) => {
      if (!token) {
        return;
      }

      api.client.patch("account/push-token", { push_token: token });
      showNotificationsSetup.value = false;
    });
}

function handleResize() {
  if (userSidebarCollapsed.value !== null) {
    sidebarSmall.value = userSidebarCollapsed.value;
    return;
  }

  sidebarSmall.value = window.innerWidth < 991;
}

function handleLoad() {
  if (userSidebarCollapsed.value !== null) {
    sidebarSmall.value = userSidebarCollapsed.value;
    return;
  }

  sidebarSmall.value = window.innerWidth < 991;
}

watch(
  () => chatStore.chatUser,
  (user) => {
    showChatDialog.value = user.id !== undefined;
  },
);

watch(showChatDialog, (value) => {
  if (!value) {
    chatStore.setChatUser({} as UserDto);
  }
});

onMounted(async () => {
  appStore.setMobile(display.mobile.value);

  const storedLocale = localStorage.getItem(localeField) || "en";
  setLocale(storedLocale);

  const authorized = await loadDispatcher();
  if (authorized) {
    await loadData();
    await connectWebSocket();
  }

  calculateTime();
  timer = setInterval(calculateTime, 5000);

  window.addEventListener("resize", handleResize);
  window.addEventListener("DOMContentLoaded", handleLoad);
});

onUnmounted(() => {
  disconnectChat();
  if (timer) {
    clearInterval(timer);
  }
});
</script>

<template>
  <v-layout :class="{ vertical_collapsed: sidebarSmall }" class="site-page">
    <gallery-view :active-id="mediaId" :items="media" :show="mediaId !== null" @close="setMediaId(null)" />
    <sidebar-component :open-threads-count="openThreadsCount" />
    <v-app-bar app color="white">
      <div class="d-flex align-center justify-space-between flex-wrap width-100 pr-3 pl-3">
        <div class="d-flex align-center">
          <!--          <v-app-bar-nav-icon class="text-grey-darken-1" @click="handleSidebarSize" />-->
          <div class="pa-0 ml-2 text-grey-darken-3 subheading font-weight-medium">
            {{ headerTitle }}
          </div>
        </div>
        <div class="d-flex align-center">
          <div class="mr-2">
            <span> {{ timezoneName }} </span>:
            <span
              ><b> {{ timezoneTime }} </b></span
            >
          </div>
          <v-btn color="grey-darken-1" icon="mdi-bell-outline"></v-btn>
          <v-menu location="bottom right" offset="10" origin="top right" transition="scale-transition">
            <template #activator="{ props }">
              <v-btn class="ml-4" v-bind="props" variant="flat">
                <v-avatar size="35">
                  <img v-if="dispatcher?.avatar" :alt="dispatcher.name || ''" :src="dispatcher.avatar" />
                  <v-icon v-else width="35">mdi-account-circle</v-icon>
                </v-avatar>
                <span class="text-grey-darken-2 body-2">
                  {{ dispatcher?.name }}
                </span>
                <v-icon class="text-grey-darken-2 ml-2" small> mdi-chevron-down</v-icon>
              </v-btn>
            </template>
            <v-list density="compact">
              <v-list-item>
                <v-list-item-title> Timezone: {{ dispatcher?.timezone }}</v-list-item-title>
              </v-list-item>
              <v-divider class="my-1" />
              <v-list-item :to="{ name: 'account-profile' }">
                <v-list-item-title>
                  {{ t("account.profile") }}
                </v-list-item-title>
              </v-list-item>
              <v-list-item :to="{ name: 'company-profile' }">
                <v-list-item-title>
                  {{ t("company.profile") }}
                </v-list-item-title>
              </v-list-item>
              <v-list-item :to="{ name: 'change-password' }">
                <v-list-item-title>
                  {{ t("auth.change-password") }}
                </v-list-item-title>
              </v-list-item>
              <v-divider class="my-1" />
              <v-list-item @click="logout">
                <v-list-item-title>
                  {{ t("account.logout") }}
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </div>
    </v-app-bar>
    <v-main>
      <router-view v-if="viewHasAccess" :key="$route.path" />
      <v-container v-else class="pt-10">
        <v-alert type="warning">You don't have access to this section</v-alert>
      </v-container>
    </v-main>
    <v-dialog
      v-model="showChatDialog"
      :no-click-animation="true"
      :persistent="true"
      :scrollable="true"
      max-width="1080"
      transition="dialog-top-transition"
    >
      <chat-dialog :user="chatUser" @close="showChatDialog = false" />
    </v-dialog>
    <omni-snackbar></omni-snackbar>
    <v-snackbar v-model="offline" :timeout="-1" color="#FF5252" location="bottom right" multi-line variant="flat">
      Lost connection to server
    </v-snackbar>
    <v-dialog v-model="showNotificationsSetup" :fullscreen="mobile" width="600">
      <v-btn class="ma-4" color="success" @click="setupNotifications"> Подключить уведомления</v-btn>
    </v-dialog>
  </v-layout>
</template>

<style lang="scss">
.site-page {
  min-height: 100vh;
}

.v-main__wrap {
  z-index: 2;
}

.mess-counter {
  position: absolute;
  height: 16px;
  top: 0;
  right: 0;
  padding: 0 4px;
  border-radius: 10px;
}

.width-auto {
  width: auto !important;
}

.header {
  color: #000;
  padding-top: 7px;
}

header {
  box-shadow: 0 8px 20px -7px rgba(0, 0, 0, 0.1) !important;
}

.v-data-table table {
  border: solid 1px #ddd;
}

.v-data-table thead tr {
  background: #e1e1e1;
}

.v-data-table th {
  font-size: 0.85rem;
}

.vue-daterange-picker {
  width: 100% !important;
}

.vue-daterange-picker .reportrange-text {
  border: 0 !important;
  padding: 0 !important;
}

.vue-daterange-picker .reportrange-text input {
  text-align: center;
}

.height-100 {
  height: 100%;
}

.width-100 {
  width: 100%;
}

.link {
  cursor: pointer;
}

.v-app-bar-title {
  margin-left: 150px;
}

fieldset {
  border-radius: 5px;
  border: solid 1px #ccc;
}

.pointer {
  cursor: pointer;
}
</style>
